import { Badge, Button, Field, Input, Switch } from '@cloudflare/kumo';
import { SidePanel } from './ui/SidePanel';
import { useCallback, useEffect, useState } from 'react';
import { PasswordField } from './ui/PasswordField';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { SocialIcon, type SocialProvider } from './SocialIcon';

interface ProviderView {
  provider: SocialProvider;
  label: string;
  clientId: string;
  hasSecret: boolean;
  enabled: boolean;
  callbackUrl: string;
}

const CONSOLE: Record<SocialProvider, string> = {
  google: 'Google Cloud › APIs e serviços › Credenciais › ID do cliente OAuth (aplicativo da Web)',
  microsoft: 'Microsoft Entra › Registros de aplicativo (contas de qualquer organização e pessoais)',
  github: 'GitHub › Settings › Developer settings › OAuth Apps',
};

/** Login social (Plataforma › Integrações) */
export function OAuthProvidersCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [editing, setEditing] = useState<ProviderView | null>(null);
  const [form, setForm] = useState({ clientId: '', clientSecret: '', enabled: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/settings/oauth');
    if (res.ok) setProviders((await res.json()).providers || []);
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const open = (p: ProviderView) => {
    setForm({ clientId: p.clientId, clientSecret: '', enabled: p.enabled || !p.clientId });
    setEditing(p);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      const res = await fetchApi('/api/settings/oauth', { method: 'PUT', body: JSON.stringify({ provider: editing.provider, ...form }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Erro ao salvar');
      setProviders(body.providers || []);
      setEditing(null);
      toast.success(`Login com ${editing.label} salvo`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success('Endereço copiado'); } catch { /* sem permissão */ }
  };

  const active = providers.filter((p) => p.enabled).length;

  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center', gap: 8 }}>
              <SocialIcon provider="google" size={20} />
              <SocialIcon provider="microsoft" size={18} />
              <SocialIcon provider="github" size={20} />
            </span>
            {active > 0 ? <Badge variant="green">{active} ativo{active > 1 ? 's' : ''}</Badge> : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>Login social</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Entrar com Google, Microsoft ou GitHub</div>
          <ul className="oauth-rows">
            {providers.map((p) => (
              <li key={p.provider}>
                <span>{p.label}</span>
                {p.enabled ? <Badge variant="green">Ativo</Badge> : p.clientId ? <Badge variant="neutral">Desligado</Badge> : <Badge variant="neutral">Sem credencial</Badge>}
                <Button variant="secondary" onClick={() => open(p)}>{p.clientId ? 'Editar' : 'Configurar'}</Button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <SidePanel open={editing !== null} onClose={() => setEditing(null)} title={editing ? `Login com ${editing.label}` : ''} description={editing ? `Crie as credenciais em ${CONSOLE[editing.provider]} e cadastre o endereço de retorno abaixo.` : undefined} formId="oauth-form" submitting={saving} submitDisabled={!editing || !form.clientId.trim() || (!editing.hasSecret && !form.clientSecret.trim())}>
        {editing && (
          <form id="oauth-form" noValidate onSubmit={save} className="flex flex-col gap-4">
            <div className="flex gap-2 items-end">
              <div style={{ flex: 1 }}>
                <Field label="Endereço de retorno (redirect URI)">
                  <Input  value={editing.callbackUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
                </Field>
              </div>
              <Button variant="secondary" icon={ICONS.Copy} type="button" onClick={() => copy(editing.callbackUrl)}>Copiar</Button>
            </div>
            <Field label="Client ID">
              <Input onChange={(e) => setForm({ ...form, clientId: e.target.value })} value={form.clientId} autoComplete="off" />
            </Field>
            <PasswordField
              label="Client secret"
              value={form.clientSecret}
              onChange={(value) => setForm({ ...form, clientSecret: value })}
              placeholder={editing.hasSecret ? 'Deixe em branco para manter o atual' : ''}
              autoComplete="new-password"
            />
            <div className="flex items-center gap-3">
              <Switch onCheckedChange={(checked: boolean) => setForm({ ...form, enabled: checked })} aria-label="Ativar" checked={form.enabled} />
              <div>Mostrar o botão na tela de login</div>
            </div>
        </form>
        )}
      </SidePanel>
    </>
  );
}
